import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import DistributionFpsDetails from './DistributionStatusReportFps';
import ExcelJS from 'exceljs';

const DistributionAbstractReport = () => {
  const [data, setData] = useState([]);
  const [loadingAbstract, setLoadingAbstract] = useState(true);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const getMonth = () => (Number(month) > 0 && Number(month) <= 12 ? Number(month) : new Date().getMonth() + 1);
  const getYear = () => (Number(year) > 2000 ? Number(year) : new Date().getFullYear());
  const getMonthName = () => new Date(getYear(), getMonth() - 1).toLocaleString('default', { month: 'long' });

  // Load district-level abstract
  const loadAbstract = async (m, y) => {
    setLoadingAbstract(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL_SIDE}/api/details`, {
        params: { month: m, year: y },
      });
      setData(res.data.data || res.data || []);
    } catch {
      alert('Failed to load data');
      setData([]);
    } finally {
      setLoadingAbstract(false);
    }
  };

  // Load current month/year first
  useEffect(() => {
    const fetchCurrentAndLoad = async () => {
      try {
        const [mRes, yRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_URL_HEADER}/Common/currentMonth`),
          axios.get(`${process.env.REACT_APP_URL_HEADER}/Common/currentYear`),
        ]);

        let m = Number(mRes?.data?.month ?? mRes?.data);
        let y = Number(yRes?.data?.year ?? yRes?.data);

        setMonth(String(m));
        setYear(String(y));
        await loadAbstract(m, y);
      } catch {
        console.error('Error fetching current month/year');
      }
    };
    fetchCurrentAndLoad();
  }, []);

  const handleDrillDown = (distCode, status) => {
    setSelected({ distCode, status });
  };


  const excelDownloadAbstract = async (status) => {
    try {
      setDownloading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_URL_SIDE}/api/excel`,
        {
          params: {
            status,
            month: getMonth(),
            year: getYear()
          },
          responseType: 'json',
        }
      );

      if (!response.data) {
        alert('No data received from server');
        return;
      }

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Distribution Report');

      // Add headers
      worksheet.columns = [
        { header: 'Sl No', key: 'slNo', width: 10, style: { alignment: { horizontal: 'center' } } },
        { header: 'District', key: 'district', width: 25 },
        { header: 'Office', key: 'office', width: 25 },
        { header: 'FPS ID', key: 'fpsId', width: 15 },
        { header: 'Dealer Name', key: 'dealer', width: 30 },
        { header: 'Device ID', key: 'deviceId', width: 15 },
        { header: 'Total Cards', key: 'totalCards', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Online Cards', key: 'onlineCards', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Partial Offline', key: 'partialOffline', width: 18, style: { alignment: { horizontal: 'right' } } },
        { header: '% Availed', key: 'onlinePercent', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Portability', key: 'portability', width: 15, style: { alignment: { horizontal: 'right' } } },
        { header: 'Total %', key: 'totalPercent', width: 12, style: { alignment: { horizontal: 'right' } } }
      ];


      // Style the header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6FA' }
      };
      // Add title row
      worksheet.insertRow(1, [`FPS Distribution Details  - ${response.data.msg || ''}`]);
      worksheet.mergeCells('A1:L1');
      worksheet.getRow(1).font = { bold: true, size: 14 };
      worksheet.getRow(1).alignment = { horizontal: 'center' };

      // Add data rows
      if (Array.isArray(response.data)) {
        response.data.forEach((item, index) => {
          worksheet.addRow({
            slNo: index + 1,
            district: item.dist_name || 'NA',
            office: item.afso_name_en || '',
            fpsId: item.fps_id || '',
            dealer: item.del_name || '',
            deviceId: item.device_id || 'NA',
            totalCards: item.total_cards || 0,
            onlineCards: item.avail_online_cards || 0,
            partialOffline: item.avail_poff_cards || 0,
            onlinePercent: item.online_trans_per || 0,
            portability: item.avail_port_cards || 0,
            totalPercent: item.trans_per || 0
          });
        });
      } else if (response.data.list && Array.isArray(response.data.list)) {
        response.data.list.forEach((item, index) => {
          worksheet.addRow({
            slNo: index + 1,
            district: item.dist_name || 'NA',
            office: item.afso_name_en || '',
            fpsId: item.fps_id || '',
            dealer: item.del_name || '',
            deviceId: item.device_id || 'NA',
            totalCards: item.total_cards || 0,
            onlineCards: item.avail_online_cards || 0,
            partialOffline: item.avail_poff_cards || 0,
            onlinePercent: item.online_trans_per || 0,
            portability: item.avail_port_cards || 0,
            totalPercent: item.trans_per || 0
          });
        });
      }

      // Add totals row
      // const dataArray = Array.isArray(response.data) ? response.data : (response.data.list || []);
      // const totals = dataArray.reduce(
      //   (acc, item) => {
      //     acc.totalFps += Number(item.total_fps || 0);
      //     acc.p1 += Number(item.per_1 || 0);
      //     acc.p2 += Number(item.per_2 || 0);
      //     acc.p3 += Number(item.per_3 || 0);
      //     acc.p4 += Number(item.per_4 || 0);
      //     return acc;
      //   },
      //   { totalFps: 0, p1: 0, p2: 0, p3: 0, p4: 0 }
      // );

      // worksheet.addRow({});
      // worksheet.addRow({
      //   district: 'TOTAL',
      //   totalCards: response.data.tot_cards,
      //   onlineCards: response.data.online_month_card,
      //   partialOffline: response.data.poff_month_card,
      //   onlinePercent: response.data.tot_online_per,
      //   portability: response.data.port_month_card,
      //   totalPercent: response.data.tot_per
      // });

      // Style the totals row
      // const totalsRow = worksheet.lastRow;
      // totalsRow.font = { bold: true };
      // totalsRow.fill = {
      //   type: 'pattern',
      //   pattern: 'solid',
      //   fgColor: { argb: 'FFF0F8FF' }
      // };

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();

      // Create and download file
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `distribution_${getMonth()}_${getYear()}_status_${status}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('Excel download error:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };
  // Totals
  const totals = data.reduce(
    (acc, d) => {
      acc.totalFps += Number(d.total_fps || 0);
      acc.p0 += Number(d.per_0 || 0);
      acc.p1 += Number(d.per_1 || 0);
      acc.p2 += Number(d.per_2 || 0);
      acc.p3 += Number(d.per_3 || 0);
      acc.p4 += Number(d.per_4 || 0);
      return acc;
    },
    { totalFps: 0, p0: 0, p1: 0, p2: 0, p3: 0, p4: 0 }
  );

  return (
    <div>

      {loadingAbstract ? (
        <CircularProgress style={{ display: 'block', margin: '40px auto' }} />
      ) : data.length === 0 ? (
        <div className="response" style={{ textAlign: 'center' }}>
          No Data Found
        </div>
      ) : (
        <table className="report-table" style={{ width: '70%', margin: '16px auto' }}>
          <thead>
            <tr className="tableheader">
              <th colSpan={8}>
                No of FPS with Distribution %  for {getMonthName()} '{getYear()}
              </th>
            </tr>
            <tr className="tableheader">
              <th>Sl No</th>
              <th>District</th>
              <th >Total</th>
              <th >0 %</th>
              <th >1 to 30%</th>
              <th >30 to 60%</th>
              <th >60 to 90%</th>
              <th >Greater than 90%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => (
              <tr key={item.dist_code || i}>
                <td>{i + 1}</td>
                <td>{item.dist_name}</td>
                <td align="right">{item.total_fps}</td>
                {[0, 1, 2, 3, 4].map((s) => (
                  <td key={s} align="right">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDrillDown(item.dist_code, s);
                      }}
                    >
                      {item[`per_${s}`]}
                    </a>
                  </td>
                ))}
              </tr>
            ))}
            <tr className="tableheader">
              <td colSpan={2}>Total</td>
              <td align="right">{totals.totalFps}</td>
              {[0, 1, 2, 3, 4].map((s) => (
                <td
                  key={s}
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                  onClick={() => excelDownloadAbstract(s)}
                  title="Download Excel for this status"
                ><u>
                    {totals[`p${s}`]}</u>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}

      {/* FPS-level table below abstract */}
      {selected && (
        <div style={{ marginTop: '40px' }}>
          <DistributionFpsDetails
            distCode={selected.distCode}
            status={selected.status}
            month={getMonth()}
            year={getYear()}
          />
        </div>
      )}

      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>

    </div>
  );
};

export default DistributionAbstractReport;
